import React from 'react';
import {
  Form as BootstrapForm,
} from 'react-bootstrap';
import PropTypes from 'prop-types';

import {
  getPassthroughProps
} from '../componentHelpers.js';

import EventSource from '../EventSource.js';

let formContextStub = {
  onStateChange: () => { },
  removeField: () => { },
  setField: () => { },
  submit: () => { },
};

class Form extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this.handleBrowserSubmit = this.handleBrowserSubmit.bind(this);
    this.handleParentContextChange = this.handleParentContextChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.removeField = this.removeField.bind(this);
    this.setField = this.setField.bind(this);

    this.contextState = {
      hasParentSubmitted: false,
      hasSubmitted: false,
      isParentSubmitting: false,
      isSubmitting: false,
    };

    this.fields = {};

    this.contextChangeEvent = new EventSource();
    this.validationWaiter = new TrueFlagWaiter(() => !this.isValidating);

    this.formContext.onStateChange(this.handleParentContextChange);
  }

  get formContext() {
    return this.context.form || formContextStub;
  }

  get isSyntheticForm() {
    return this.props.formType === 'super';
  }

  get isValid() {
    return Object.keys(this.fields).every(key => this.fields[key]);
  }

  get isValidating() {
    return Object.keys(this.fields).some(key => this.fields[key] === null);
  }

  getChildContext() {
    return {
      form: {
        isSyntheticForm: this.isSyntheticForm,
        loading: this.props.loading,
        onStateChange: this.contextChangeEvent.subscribe,
        removeField: this.removeField,
        setField: this.setField,
        submit: this.handleSubmit,
      }
    };
  }

  render() {
    return this.isSyntheticForm
      ? <div { ...getPassthroughProps(this) }>{this.props.children}</div>
      : <BootstrapForm { ...getPassthroughProps(this) } autoComplete={this.props.autoComplete} onSubmit={this.handleBrowserSubmit}>{this.props.children}</BootstrapForm>;
  }

  setField(component, isValid) {
    if (this.fields[component] === isValid) {
      return;
    }

    this.fields[component] = isValid;
    this.formContext.setField(component, isValid);
    this.validationWaiter.pullChanges();
    this.contextChangeEvent.publish(this.getChildContextState());
  }

  removeField(component) {
    delete this.fields[component];
    this.formContext.removeField(component);
    this.validationWaiter.pullChanges();
    this.contextChangeEvent.publish(this.getChildContextState());
  }

  componentDidUpdate() {
    this.contextChangeEvent.publish(this.getChildContextState());
  }

  getChildContextState() {
    return {
      hasSubmitted: this.contextState.hasSubmitted || this.contextState.hasParentSubmitted,
      isSubmitting: this.contextState.isSubmitting || this.contextState.isParentSubmitting,
      isValid: this.isValid,
      isValidating: this.isValidating,
      loading: this.props.loading
    };
  }

  handleParentContextChange(context) {
    if (context.hasSubmitted !== this.contextState.hasParentSubmitted) {
      this.setContextState({ hasParentSubmitted: context.hasSubmitted });
    }

    if (context.isSubmitting !== this.contextState.isParentSubmitting) {
      this.setContextState({ isParentSubmitting: context.isSubmitting });
    }
  }

  setContextState(stateChange) {
    this.contextState = Object.assign({}, this.contextState, stateChange);
    this.contextChangeEvent.publish(this.getChildContextState());
  }

  handleBrowserSubmit(event) {
    event.preventDefault();
    this.handleSubmit();
  }

  handleSubmit() {
    this.setContextState({ hasSubmitted: true });

    if (this.contextState.isSubmitting) {
      return;
    }

    this.setContextState({ isSubmitting: true });

    this.validationWaiter.wait()
      .then(() => {
        if (this.isValid) {
          return this.props.onSubmit({
            superFormSubmit: this.formContext.submit,
            resetFormSubmit: () => this.setContextState({ hasSubmitted: false }),
          });
        }
      })
      .then(() => {
        this.setContextState({ isSubmitting: false });
      }, (err) => {
        console.error(err);
        this.setContextState({ isSubmitting: false });
      });
  }
}

Form.propTypes = {
  autoComplete: PropTypes.string,
  formType: PropTypes.string,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
};

Form.defaultProps = {
  autoComplete: 'off',
  formType: 'normal',
  loading: false,
  onSubmit: () => { },
};

Form.contextTypes = {
  form: PropTypes.object,
};

Form.childContextTypes = {
  form: PropTypes.object,
};

export default Form;

class TrueFlagWaiter {
  constructor(getFlagValue) {
    if (typeof getFlagValue !== 'function') {
      throw new Error('Must pass getFlagValue function');
    }

    this._getFlagValue = getFlagValue;
    this._value = this._getFlagValue();

    this._resetPromise();
    if (this._value) {
      this._resolve();
    }
  }

  wait() {
    return this._promise;
  }

  pullChanges() {
    if (!this._checkHasChanged()) {
      return;
    }

    if (this._value) {
      this._resolve();
    }
    else {
      this._resetPromise();
    }
  }

  _checkHasChanged() {
    let oldValue = this._value;
    this._value = this._getFlagValue();
    return this._value !== oldValue;
  }

  _resetPromise() {
    this._promise = new Promise((res) => {
      this._resolve = res;
    });
  }
}
